import {
  VENUE_ADDRESS_FORMATS,
  VENUE_ADDRESS_GENERIC_FORMAT,
  VENUE_ADDRESS_LOCAL_FORMAT_COUNTRIES,
  VENUE_ADDRESS_PART_ERROR_MESSAGES,
  VENUE_ADDRESS_PART_MAX_LENGTH,
  type VenueAddressCountryName,
  type VenueAddressFormat,
  type VenueAddressPartError,
  type VenueAddressPartKey,
} from '@/features/metadata-library/config/venueAddressFormats';
import type { VenueFormValues } from '@/features/metadata-library/types/libraryFormValues';
import { ISO_COUNTRY_CODES } from '@/shared/config/countries';
import { i18n } from '@lingui/core';

function hasLocalAddressFormat(value: string): value is VenueAddressCountryName {
  return (VENUE_ADDRESS_LOCAL_FORMAT_COUNTRIES as readonly string[]).includes(value);
}

function emptyAddressParts(): Pick<VenueFormValues, VenueAddressPartKey> {
  return {
    addressLine1: '',
    subLocality: '',
    locality: '',
    adminArea: '',
  };
}

/** The country's address layout, or `null` when the country name is not in the ISO catalog. */
export function venueAddressFormat(countryName: string): VenueAddressFormat | null {
  if (!(ISO_COUNTRY_CODES as readonly string[]).includes(countryName)) return null;
  return hasLocalAddressFormat(countryName)
    ? VENUE_ADDRESS_FORMATS[countryName]
    : VENUE_ADDRESS_GENERIC_FORMAT;
}

/** Complete country-picker items alphabetized for the active locale. */
export function venueCountryItems(locale: string) {
  const collator = new Intl.Collator(locale);
  return ISO_COUNTRY_CODES.map((countryName) => ({
    value: countryName,
    label: countryName,
  })).sort((left, right) => collator.compare(left.label, right.label));
}

function venueAddressPartErrorCode(
  format: VenueAddressFormat,
  key: VenueAddressPartKey,
  value: string,
): VenueAddressPartError | null {
  const part = format.parts.find((entry) => entry.key === key);
  if (!part) return null;

  const trimmed = value.trim();
  if (!trimmed) return part.required ? 'required' : null;
  if (/[\r\n]/.test(value)) return 'singleLine';
  if (Array.from(trimmed).length > VENUE_ADDRESS_PART_MAX_LENGTH) return 'tooLong';
  return null;
}

/** The error for one visible address part, or `undefined` when valid. */
export function venueAddressPartError(
  format: VenueAddressFormat,
  key: VenueAddressPartKey,
  value: string,
): string | undefined {
  const error = venueAddressPartErrorCode(format, key, value);
  return error ? i18n._(VENUE_ADDRESS_PART_ERROR_MESSAGES[error]) : undefined;
}

/** Every invalid visible part in display order. */
export function venueAddressErrors(values: VenueFormValues) {
  const format = venueAddressFormat(values.addressCountry);
  if (!format) return [];
  return format.parts.flatMap((part) => {
    const message = venueAddressPartError(format, part.key, values[part.key]);
    return message ? [{ key: part.key, message }] : [];
  });
}

/** True once all country-required address parts have values. */
export function isVenueAddressComplete(values: VenueFormValues): boolean {
  const format = venueAddressFormat(values.addressCountry);
  return Boolean(format?.parts.every((part) => !part.required || values[part.key].trim()));
}

/** One-line address in country order, without a postal-code component. */
export function composeVenueAddress(values: VenueFormValues): string | null {
  const format = venueAddressFormat(values.addressCountry);
  if (!format || !isVenueAddressComplete(values)) return null;

  const parts = format.parts
    .map((part) => values[part.key].trim())
    .filter(Boolean)
    .map(encodeAddressSegment);
  const countryName = values.addressCountry;
  return (format.countryFirst ? [countryName, ...parts] : [...parts, countryName]).join(', ');
}

/** Quotes a component only when required to retain commas or quotation marks on a later edit. */
function encodeAddressSegment(value: string): string {
  return /[,\"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/**
 * Parses the comma-separated format produced above, including its CSV-style quoted
 * segments. Older unquoted addresses remain valid as long as their components did
 * not contain delimiters.
 */
function parseAddressSegments(address: string): string[] | null {
  const segments: string[] = [];
  let segment = '';
  let quoted = false;
  let quoteClosed = false;

  for (let index = 0; index < address.length; index += 1) {
    const character = address[index] ?? '';
    if (quoted) {
      if (character !== '"') {
        segment += character;
        continue;
      }
      if (address[index + 1] === '"') {
        segment += '"';
        index += 1;
        continue;
      }
      quoted = false;
      quoteClosed = true;
      continue;
    }

    if (quoteClosed) {
      if (/\s/.test(character)) continue;
      if (character !== ',') return null;
      segments.push(segment.trim());
      segment = '';
      quoteClosed = false;
      continue;
    }

    if (character === ',') {
      segments.push(segment.trim());
      segment = '';
      continue;
    }
    if (character === '"') {
      if (segment.trim()) return null;
      segment = '';
      quoted = true;
      continue;
    }
    segment += character;
  }

  if (quoted) return null;
  segments.push(segment.trim());
  return segments.some((entry) => !entry) ? null : segments;
}

function parseForCountry(
  address: string,
  countryName: string,
): Pick<VenueFormValues, 'addressCountry' | VenueAddressPartKey> | null {
  const format = venueAddressFormat(countryName);
  if (!format) return null;

  const segments = parseAddressSegments(address);
  if (!segments) return null;

  const countryIndex = format.countryFirst ? 0 : segments.length - 1;
  if (
    segments[countryIndex]?.localeCompare(countryName, undefined, { sensitivity: 'accent' }) !== 0
  ) {
    return null;
  }

  const values = format.countryFirst ? segments.slice(1) : segments.slice(0, -1);
  const requiredParts = format.parts.filter((part) => part.required);
  const parts = emptyAddressParts();

  if (values.length === format.parts.length) {
    format.parts.forEach((part, index) => {
      parts[part.key] = values[index] ?? '';
    });
  } else if (values.length === requiredParts.length) {
    requiredParts.forEach((part, index) => {
      parts[part.key] = values[index] ?? '';
    });
  } else {
    return null;
  }

  return { addressCountry: countryName, ...parts };
}

/**
 * Parses only the deterministic address format written by this frontend. Ambiguous
 * legacy text deliberately returns `null` instead of guessing at address parts.
 */
export function parseVenueAddress(
  address: string,
): Pick<VenueFormValues, 'addressCountry' | VenueAddressPartKey> | null {
  const normalizedAddress = address.trim();
  if (!normalizedAddress) return null;

  return (
    ISO_COUNTRY_CODES.map((countryName) => parseForCountry(normalizedAddress, countryName)).find(
      (parsed): parsed is Pick<VenueFormValues, 'addressCountry' | VenueAddressPartKey> =>
        parsed !== null,
    ) ?? null
  );
}

/** Form values for a stored address, retaining raw legacy text when it cannot be split safely. */
export function venueAddressValues(
  address: string | null | undefined,
): Pick<VenueFormValues, 'address' | 'addressCountry' | VenueAddressPartKey> {
  const rawAddress = address ?? '';
  return {
    address: rawAddress,
    ...(parseVenueAddress(rawAddress) ?? { addressCountry: '', ...emptyAddressParts() }),
  };
}
